import React, { useState, useEffect } from 'react';
import { Button, Input, Textarea, Card } from '../../components';
import * as pitchService from '../../services/pitch-service';

type IdeaStatus = 'draft' | 'submitted' | 'approved' | 'revision_needed';

interface IdeaForm {
  title: string;
  problemStatement: string;
  proposedSolution: string;
  targetMarket: string;
  uniqueValueProposition: string;
}

const statusConfig: Record<IdeaStatus, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'bg-surface text-muted' },
  submitted: { label: 'Submitted', color: 'bg-info/15 text-info' },
  approved: { label: 'Approved', color: 'bg-success/15 text-success' },
  revision_needed: { label: 'Revision Needed', color: 'bg-warning/15 text-warning' },
};

const IdeaSubmission: React.FC = () => {
  const [status, setStatus] = useState<IdeaStatus | null>(null);
  const [mentorFeedback, setMentorFeedback] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState<IdeaForm>({
    title: '',
    problemStatement: '',
    proposedSolution: '',
    targetMarket: '',
    uniqueValueProposition: '',
  });

  useEffect(() => {
    pitchService.getMyIdeas().then((ideas: any[]) => {
      if (ideas.length > 0) {
        const latest = ideas[0];
        setStatus(latest.status);
        setMentorFeedback(latest.mentorFeedback || '');
        setForm({
          title: latest.title || '',
          problemStatement: latest.problemStatement || '',
          proposedSolution: latest.proposedSolution || '',
          targetMarket: latest.targetMarket || '',
          uniqueValueProposition: latest.uniqueValueProp || '',
        });
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const set = (field: keyof IdeaForm, value: string) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const data = { ...form, uniqueValueProp: form.uniqueValueProposition, status: 'draft' };
      await pitchService.submitIdea(data);
      setStatus('draft');
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.problemStatement.trim() || !form.proposedSolution.trim()) return;
    setSubmitting(true);
    try {
      const data = { ...form, uniqueValueProp: form.uniqueValueProposition, status: 'submitted' };
      await pitchService.submitIdea(data);
      setStatus('submitted');
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const isReadOnly = status === 'submitted' || status === 'approved';

  if (loading) {
    return <div className="section-gap"><p className="text-muted">Loading...</p></div>;
  }

  return (
    <div className="section-gap">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-text">Idea Submission</h1>
          <p className="text-muted mt-1">Submit your startup idea for mentor review</p>
        </div>
        {status && (
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusConfig[status].color}`}>
            {statusConfig[status].label}
          </span>
        )}
      </div>

      {status === 'revision_needed' && mentorFeedback && (
        <Card className="border-l-4 border-l-warning bg-warning/5">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-warning mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="font-medium text-warning mb-1">Mentor Feedback</h3>
              <p className="text-text text-sm">{mentorFeedback}</p>
            </div>
          </div>
        </Card>
      )}

      <Card>
        <div className="space-y-5">
          <Input label="Title" name="title" placeholder="Give your idea a clear, concise title" value={form.title} onChange={e => set('title', e.target.value)} disabled={isReadOnly} required />
          <Textarea label="Problem Statement" name="problemStatement" placeholder="What problem are you solving? Who experiences this problem?" value={form.problemStatement} onChange={e => set('problemStatement', e.target.value)} rows={4} disabled={isReadOnly} required />
          <Textarea label="Proposed Solution" name="proposedSolution" placeholder="How does your solution address the problem?" value={form.proposedSolution} onChange={e => set('proposedSolution', e.target.value)} rows={4} disabled={isReadOnly} required />
          <Input label="Target Market" name="targetMarket" placeholder="Who are your target customers?" value={form.targetMarket} onChange={e => set('targetMarket', e.target.value)} disabled={isReadOnly} />
          <Textarea label="Unique Value Proposition" name="uniqueValueProposition" placeholder="What makes your solution different from existing alternatives?" value={form.uniqueValueProposition} onChange={e => set('uniqueValueProposition', e.target.value)} rows={3} disabled={isReadOnly} />
        </div>
      </Card>

      {!isReadOnly && (
        <div className="flex items-center gap-3 justify-end">
          <Button variant="ghost" onClick={handleSaveDraft} disabled={saving}>
            {saving ? 'Saving...' : 'Save Draft'}
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !form.title.trim()}>
            {submitting ? 'Submitting...' : 'Submit for Review'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default IdeaSubmission;
